import { useState, useRef, useCallback, useEffect } from 'react';
import { productsAPI } from '../services/api';

const useProductSearch = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const debounceRef = useRef(null);
  const dropdownRef = useRef(null);

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) {
      setResults(null);
      setIsOpen(false);
      return;
    }

    setLoading(true);
    try {
      const res = await productsAPI.crossSearch({ q, quick: 'true', limit: 8 });
      setResults(res.data);
      setIsOpen(true);
    } catch (err) {
      console.error('Search error:', err);
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleQueryChange = useCallback((value) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (value.length < 2) {
      setResults(null);
      setIsOpen(false);
      return;
    }

    debounceRef.current = setTimeout(() => {
      search(value);
    }, 300);
  }, [search]);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const clear = useCallback(() => {
    setQuery('');
    setResults(null);
    setIsOpen(false);
  }, []);

  // Click outside detection
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return {
    query,
    setQuery: handleQueryChange,
    results,
    loading,
    isOpen,
    close,
    clear,
    dropdownRef
  };
};

export default useProductSearch;
